import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import {
    normalizeTemplateSource,
    formatTemplateSourceLabel,
    formatIsDefaultLabel,
    formatTemplateDetailDate,
    normalizeIsDefault,
} from './dailyMenuApi';
import MenuTemplateItemsEditor from './MenuTemplateItemsEditor';
import { fetchMenuTemplateById, updateMenuTemplate } from './menuTemplateItemUtils';
import './css/Home.css';
import './css/Menu.css';
import './css/DailyMenu.css';
import './css/Inventory.css';
import './css/Restaurants.css';
import './css/OrderDetails.css';

const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

function formatDayOfWeekDisplay(day) {
    if (day == null || String(day).trim() === '') return '—';
    const s = String(day).trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatStatusDisplay(status) {
    if (status == null || String(status).trim() === '') return '—';
    const s = String(status).trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function isActiveStatus(status) {
    return String(status ?? '').trim().toLowerCase() === 'active';
}

function apiDayToFormDay(day) {
    return formatDayOfWeekDisplay(day) || 'Monday';
}

function apiStatusToFormStatus(status) {
    return formatStatusDisplay(status) || 'Active';
}

const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
};

const DetailField = ({ label, value, wide = false, children }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
        <span className="restaurant-detail-label">{label}</span>
        {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
    </div>
);

function MenuTemplateDetailRouteRedirect({ templateId }) {
    const [searchParams] = useSearchParams();
    const restaurantId = searchParams.get('restaurantId');
    let to = `/home?tab=restaurants/mytemplates/${templateId}`;
    if (restaurantId) {
        to += `&restaurantId=${encodeURIComponent(restaurantId)}`;
    }
    return <Navigate to={to} replace />;
}

const MenuTemplateDetailView = ({
    templateId,
    embedded = false,
}) => {
    const [searchParams] = useSearchParams();
    const restaurantId = searchParams.get('restaurantId') || localStorage.getItem('restaurantId');

    const [template, setTemplate] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDayOfWeek, setEditDayOfWeek] = useState('Monday');
    const [editStatus, setEditStatus] = useState('Active');
    const [editIsDefault, setEditIsDefault] = useState(true);

    const menuSourceFilter = useMemo(() => {
        if (template?.templateSource) {
            return normalizeTemplateSource(template.templateSource);
        }
        const raw = String(searchParams.get('menuSource') ?? 'MYEATS').trim().toUpperCase();
        return raw === 'JREATS' ? 'JREATS' : 'MYEATS';
    }, [searchParams, template?.templateSource]);

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch (e) {
            return null;
        }
    }, []);

    const loadTemplate = useCallback(async () => {
        const id = Number(templateId);
        if (!templateId || Number.isNaN(id)) {
            setError('Invalid template id.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await fetchMenuTemplateById(id);
            setTemplate(data);
            setItems(Array.isArray(data.items) ? data.items : []);
        } catch (err) {
            console.error('Template detail fetch failed:', err?.response?.data || err?.message);
            setError(
                err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    'Failed to load template.'
            );
            setTemplate(null);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [templateId]);

    useEffect(() => {
        loadTemplate();
    }, [loadTemplate]);

    const openEditModal = () => {
        if (!template) return;
        setEditName(template.templateName ?? '');
        setEditDayOfWeek(apiDayToFormDay(template.dayOfWeek));
        setEditStatus(apiStatusToFormStatus(template.status));
        setEditIsDefault(normalizeIsDefault(template.isDefault));
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        if (saving) return;
        setShowEditModal(false);
    };

    const hideStatusOnEdit = isActiveStatus(template?.status);

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        if (!template?.templateId || saving) return;
        const name = editName.trim();
        if (!name) {
            alert('Template name is required.');
            return;
        }
        const userId = Number(user?.userId) || Number(restaurantId) || 1;
        const updateStatus = hideStatusOnEdit
            ? String(template.status ?? 'ACTIVE').trim().toUpperCase()
            : editStatus.toUpperCase();

        setSaving(true);
        try {
            const response = await updateMenuTemplate(template.templateId, {
                restaurantId: Number(restaurantId || template.restaurantId),
                dayOfWeek: editDayOfWeek,
                templateName: name,
                templateSource: normalizeTemplateSource(
                    template.templateSource ?? menuSourceFilter
                ),
                isDefault: editIsDefault,
                status: updateStatus,
                updatedById: userId,
            });
            alert(response.data?.message || 'Menu template updated.');
            setShowEditModal(false);
            await loadTemplate();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Failed to update template.';
            alert(`Error: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    const templateSourceLabel = formatTemplateSourceLabel(
        template?.templateSource ?? menuSourceFilter
    );

    const detailBody = (
        <div className="menu menu-template menu-template-detail-page">
            <div className="restaurant-detail-panel">
                <div className="restaurant-detail-card">
                    <div className="restaurant-detail-card-header menu-template-detail-card-header">
                        <h3 className="restaurant-detail-section-title">
                            Template information
                        </h3>
                        {!loading && !error && template ? (
                            <div className="restaurant-detail-card-header-actions">
                                <button
                                    type="button"
                                    className="edit-button"
                                    onClick={openEditModal}
                                >
                                    Edit
                                </button>
                            </div>
                        ) : null}
                    </div>

                    {loading ? (
                        <p className="order-details-loading">Loading template…</p>
                    ) : null}
                    {!loading && error ? (
                        <p className="restaurant-detail-message error">{error}</p>
                    ) : null}
                    {!loading && !error && template ? (
                        <div className="restaurant-detail-grid">
                            <DetailField label="Template name" value={template.templateName} />
                            <DetailField label="Source" value={templateSourceLabel} />
                            <DetailField
                                label="Day"
                                value={formatDayOfWeekDisplay(template.dayOfWeek)}
                            />
                            <DetailField
                                label="Default template"
                                value={formatIsDefaultLabel(template.isDefault)}
                            />
                            <DetailField label="Status">
                                <span
                                    className={`menu-template-status-badge menu-template-status-badge--${
                                        isActiveStatus(template.status) ? 'active' : 'inactive'
                                    }`}
                                >
                                    {formatStatusDisplay(template.status)}
                                </span>
                            </DetailField>
                            <DetailField label="Template ID" value={template.templateId} />
                            <DetailField
                                label="Created"
                                value={formatTemplateDetailDate(template.createdDate)}
                            />
                            <DetailField
                                label="Last updated"
                                value={formatTemplateDetailDate(template.updatedDate)}
                            />
                        </div>
                    ) : null}
                    {!loading && !template && !error ? (
                        <p className="order-details-empty">No detail data for this template.</p>
                    ) : null}
                </div>

                {!loading && !error && template ? (
                    <div className="restaurant-detail-card">
                        <MenuTemplateItemsEditor
                            restaurantId={restaurantId}
                            menuSource={menuSourceFilter}
                            items={items}
                            onItemsChange={setItems}
                            templateId={templateId}
                            user={user}
                            disabled={loading}
                            headerClassName="restaurant-detail-card-header menu-template-items-editor-header"
                        />
                    </div>
                ) : null}
            </div>

            {showEditModal && template ? (
                <div
                    className="inventory-modal-overlay"
                    onClick={closeEditModal}
                    role="presentation"
                >
                    <div
                        className="inventory-modal menu-template-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="menu-template-detail-edit-title"
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4 id="menu-template-detail-edit-title">Edit Template</h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={closeEditModal}
                                aria-label="Close"
                                disabled={saving}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <form
                            className="inventory-form menu-template-modal-form"
                            onSubmit={handleSaveTemplate}
                        >
                            <div className="inventory-form-group">
                                <label htmlFor="template-detail-edit-name">Template name</label>
                                <input
                                    id="template-detail-edit-name"
                                    type="text"
                                    className="add-menu-form-field"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    required
                                    disabled={saving}
                                />
                            </div>
                            <div className="inventory-form-group">
                                <label htmlFor="template-detail-edit-day">Day of week</label>
                                <div className="inventory-native-select-wrap">
                                    <select
                                        id="template-detail-edit-day"
                                        className="add-menu-form-field inventory-select-native"
                                        value={editDayOfWeek}
                                        onChange={(e) => setEditDayOfWeek(e.target.value)}
                                        disabled={saving}
                                    >
                                        {DAYS_OF_WEEK.map((day) => (
                                            <option key={day} value={day}>
                                                {day}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="inventory-form-group">
                                <label htmlFor="template-detail-edit-default">
                                    Default for this day
                                </label>
                                <div className="inventory-native-select-wrap">
                                    <select
                                        id="template-detail-edit-default"
                                        className="add-menu-form-field inventory-select-native"
                                        value={editIsDefault ? '1' : '0'}
                                        onChange={(e) =>
                                            setEditIsDefault(normalizeIsDefault(e.target.value))
                                        }
                                        disabled={saving}
                                    >
                                        <option value="0">No</option>
                                        <option value="1">Yes</option>
                                    </select>
                                </div>
                            </div>
                            {!hideStatusOnEdit ? (
                                <div className="inventory-form-group">
                                    <label htmlFor="template-detail-edit-status">Status</label>
                                    <div className="inventory-native-select-wrap">
                                        <select
                                            id="template-detail-edit-status"
                                            className="add-menu-form-field inventory-select-native"
                                            value={editStatus}
                                            onChange={(e) => setEditStatus(e.target.value)}
                                            disabled={saving}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            ) : null}
                            <div className="inventory-form-actions menu-template-modal-actions">
                                <button type="submit" className="add-button" disabled={saving}>
                                    {saving ? 'Saving…' : 'Update Template'}
                                </button>
                                <button
                                    type="button"
                                    className="close-button"
                                    onClick={closeEditModal}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </div>
    );

    if (embedded) {
        return detailBody;
    }

    return (
        <div className="home-container menu-template-detail-page">
            <div className="tab-content">{detailBody}</div>
        </div>
    );
};

function MenuTemplateDetail(props) {
    const { templateId: templateIdProp, embedded = false } = props;
    const { templateId: routeTemplateId } = useParams();

    if (!embedded && routeTemplateId && templateIdProp == null) {
        return <MenuTemplateDetailRouteRedirect templateId={routeTemplateId} />;
    }

    return (
        <MenuTemplateDetailView
            {...props}
            templateId={templateIdProp ?? routeTemplateId}
        />
    );
}

export default MenuTemplateDetail;
